import { useState, useEffect, useRef } from 'react';
import { fetchClient } from '@/lib/api';

export interface PersonResult {
  id: string;
  displayName: string;
  email: string | null;
  jobTitle: string | null;
  department: string | null;
}

/**
 * Debounced people search via the People API.
 * Filters out emails in `excludeEmails` from results.
 */
export function usePeopleSearch(query: string, excludeEmails: string[]) {
  const [results, setResults] = useState<PersonResult[]>([]);
  const [searching, setSearching] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);

  // Stable dependency for the exclude set
  const excludeKey = excludeEmails.join(',');

  useEffect(() => {
    let active = true;

    if (!query || query.length < 2) {
      setResults([]);
      return;
    }

    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    const currentRequestId = ++requestIdRef.current;
    const existing = new Set(excludeEmails.map(e => e.toLowerCase()));

    timeoutRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data, error } = await fetchClient.GET("/api/v1/people", {
          params: { query: { search: query, top: 8, filter_org_users: true } },
        });
        // Discard stale or post-unmount responses
        if (currentRequestId !== requestIdRef.current || !active) return;
        if (!error && data) {
          const resp = data as { people?: Array<Record<string, unknown>> };
          const mapped: PersonResult[] = (resp.people || [])
            .map(p => ({
              id: String(p.id ?? ''),
              displayName: String(p.displayName || p.userPrincipalName || 'Unknown'),
              email: typeof p.email === 'string' ? p.email
                : typeof p.userPrincipalName === 'string' ? p.userPrincipalName
                : null,
              jobTitle: typeof p.jobTitle === 'string' ? p.jobTitle : null,
              department: typeof p.department === 'string' ? p.department : null,
            }))
            .filter(p => p.email && !existing.has(p.email.toLowerCase()));
          setResults(mapped);
        }
      } catch {
        if (currentRequestId === requestIdRef.current && active) setResults([]);
      } finally {
        if (currentRequestId === requestIdRef.current && active) setSearching(false);
      }
    }, 300);

    return () => {
      active = false;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [query, excludeKey]);

  return { results, searching };
}
